/**
 * report — the pure logic behind the Practice Ops report page (plan §3 + Phase 5, 2026-10-30).
 *
 * WHAT THE SERVER ALREADY DID. POST /get-practice-pulse-report `{ siteID, from, to, provNum? }` answers
 * `{ visits[], tiles, daily[], providers[], thresholdMin }` (practice-pulse-service lib/visits.js). The tiles, the
 * per-day numbers and each visit's wait / late / in-chair / level are the SERVER's, computed with the one wait rule
 * (waitRules.js is its mirror). Nothing here recomputes them. This file only:
 *   1. turns a preset ("Last 7 days") into `{ from, to }` for the practice's own calendar day (never a browser Date);
 *   2. checks the request the way the server does (real dates, from <= to, at most 366 days, provNum a positive integer);
 *   3. words the numbers (tiles, rows, chart series) and builds the CSV matrix.
 * The ONLY values the server's response does not carry, and so are derived here from its visits (and flagged where used):
 *   - the average BOOKED length per provider (the "Booked vs actual chair time" chart, from `visits[].bookedMin`),
 *   - the average wait per HOUR for a one-day report (the design draws "per hour" for Today),
 *   - the LIVE wait of a patient who has arrived today and is not seated yet (the board shows "Waiting N min"; the server's
 *     report has no wait for a visit that never reached a room). That one goes through the injected `rules` (waitRules.js)
 *     so the board and the report can never use two rules.
 *
 * DATES ARE STRINGS. `todayYmd` is the practice's calendar day ('YYYY-MM-DD', siteDate.js siteLocalDate with
 * GENERAL_TIMEZONE); every range here is plain day arithmetic on those strings (UTC calendar maths on the parts), so a
 * browser in another zone, or a day that has 23 / 25 hours, can never move a boundary.
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file (the same convention as waitRules.js,
 * boardText.js, siteDate.js). Anything it needs from another util is passed in (`rules`, `roomLabel`).
 */

/** The server's span limit (practice-pulse-service index.js MAX_REPORT_DAYS). The page checks it first so the message is ours. */
export const MAX_REPORT_DAYS = 366;

/** The server's stand-in for "this office set no waiting limit" (lib/visits.js DEFAULT_THRESHOLD_MIN). */
export const NO_THRESHOLD_MIN = 999;

/** `?view=report` — a page of its own inside Practice Ops. NOT one of viewPref.js VIEWS: it is never stored as the view preference. */
export const REPORT_VIEW = 'report';

/** The range pills, in the design's order (plus Custom, which reveals the two date inputs). */
export const REPORT_PRESETS = Object.freeze([
  Object.freeze({ key: 'today', label: 'Today' }),
  Object.freeze({ key: 'yesterday', label: 'Yesterday' }),
  Object.freeze({ key: 'last7', label: 'Last 7 days' }),
  Object.freeze({ key: 'last30', label: 'Last 30 days' }),
  Object.freeze({ key: 'thisMonth', label: 'This month' }),
  Object.freeze({ key: 'custom', label: 'Custom' }),
]);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SITE_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86400000;

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const round1 = (n) => Math.round(n * 10) / 10;

// ── ?view=report ─────────────────────────────────────────────────────────────────────────────────

/** True when a route query (or a bare string) asks for the report: `?view=report`, first of a repeated param, any case. */
export function isReportQuery(query) {
  let raw = query;
  if (query && typeof query === 'object') raw = Array.isArray(query.view) ? query.view[0] : query.view;
  return typeof raw === 'string' && raw.trim().toLowerCase() === REPORT_VIEW;
}

// ── days ─────────────────────────────────────────────────────────────────────────────────────────

function partsOf(ymd) {
  const m = typeof ymd === 'string' ? YMD_RE.exec(ymd) : null;
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const back = new Date(Date.UTC(y, mo - 1, d));
  // a day that did not survive the round trip (2026-02-31, 2026-13-01) is not a day
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null;
  return { y, m: mo, d };
}

/** A REAL calendar day written 'YYYY-MM-DD'. */
export function isYmd(value) {
  return partsOf(value) !== null;
}

function utcOf(ymd) {
  const p = partsOf(ymd);
  return p ? Date.UTC(p.y, p.m - 1, p.d) : null;
}

function ymdOfUtc(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** `ymd` plus `n` whole days (negative goes back); null for a bad day. */
export function addDays(ymd, n) {
  const ms = utcOf(ymd);
  return ms === null || !isNum(n) ? null : ymdOfUtc(ms + Math.round(n) * DAY_MS);
}

/** Whole days from `a` to `b` (b - a); null for a bad day. */
export function daysBetween(a, b) {
  const x = utcOf(a);
  const y = utcOf(b);
  return x === null || y === null ? null : Math.round((y - x) / DAY_MS);
}

/** The first day of `ymd`'s month; null for a bad day. */
export function firstOfMonth(ymd) {
  const p = partsOf(ymd);
  return p ? `${p.y}-${pad2(p.m)}-01` : null;
}

/**
 * The period a pill stands for, on the practice's `todayYmd`. → { key, from, to } or null (a bad today, an unknown preset, or a
 * Custom range with a missing / unreal date). A Custom range is passed through AS TYPED — a reversed or over-long one is NOT
 * silently fixed; buildReportBody refuses it with a message the page shows.
 *   today / yesterday     that one day
 *   last7 / last30        today - 6 / today - 29  →  today
 *   thisMonth             the 1st  →  today (the days after today have nothing to show)
 */
export function rangeForPreset(key, todayYmd, custom) {
  if (!isYmd(todayYmd)) return null;
  let from;
  let to;
  if (key === 'today') { from = todayYmd; to = todayYmd; }
  else if (key === 'yesterday') { from = addDays(todayYmd, -1); to = from; }
  else if (key === 'last7') { from = addDays(todayYmd, -6); to = todayYmd; }
  else if (key === 'last30') { from = addDays(todayYmd, -29); to = todayYmd; }
  else if (key === 'thisMonth') { from = firstOfMonth(todayYmd); to = todayYmd; }
  else if (key === 'custom') {
    const c = obj(custom);
    if (!isYmd(c.from) || !isYmd(c.to)) return null;
    from = c.from;
    to = c.to;
  } else return null;
  return { key, from, to };
}

/** 'Fri, Oct 30' for a day; '' for a bad one. */
export function dayText(ymd) {
  const p = partsOf(ymd);
  if (!p) return '';
  return `${WEEKDAYS[new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay()]}, ${MONTHS[p.m - 1]} ${p.d}`;
}

/** 'Oct 30' for a day; '' for a bad one. */
export function shortDayText(ymd) {
  const p = partsOf(ymd);
  return p ? `${MONTHS[p.m - 1]} ${p.d}` : '';
}

/** "Oct 30, 2026" · "Oct 24 – 30, 2026" · "Sep 27 – Oct 3, 2026" · "Dec 28, 2025 – Jan 3, 2026". '' for a bad range. */
export function rangeText(from, to) {
  const a = partsOf(from);
  const b = partsOf(to);
  if (!a || !b) return '';
  if (from === to) return `${MONTHS[a.m - 1]} ${a.d}, ${a.y}`;
  if (a.y !== b.y) return `${MONTHS[a.m - 1]} ${a.d}, ${a.y} – ${MONTHS[b.m - 1]} ${b.d}, ${b.y}`;
  if (a.m === b.m) return `${MONTHS[a.m - 1]} ${a.d} – ${b.d}, ${b.y}`;
  return `${MONTHS[a.m - 1]} ${a.d} – ${MONTHS[b.m - 1]} ${b.d}, ${b.y}`;
}

// ── the request ──────────────────────────────────────────────────────────────────────────────────

/** A positive safe integer from a number or a plain digit string, else null. */
function positiveInt(value) {
  let n = null;
  if (typeof value === 'number') n = value;
  else if (typeof value === 'string' && /^\s*\d+\s*$/.test(value)) n = Number(value.trim());
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/**
 * The request body for the range report, checked the way the server checks it so a bad range never costs a round trip and the
 * message is ours. `provNum` is left out for "All providers" (undefined / null / '' / 'all').
 * @returns {{ok: true, body: {siteID: string, from: string, to: string, provNum?: number}, days: number} | {ok: false, error: string}}
 */
export function buildReportBody({ siteID, from, to, provNum } = {}) {
  if (typeof siteID !== 'string' || !SITE_ID_RE.test(siteID)) return { ok: false, error: 'The practice is not loaded yet.' };
  if (!isYmd(from) || !isYmd(to)) return { ok: false, error: 'Pick a start date and an end date.' };
  if (to < from) return { ok: false, error: 'The start date must be on or before the end date.' };
  const days = daysBetween(from, to) + 1;
  if (days > MAX_REPORT_DAYS) return { ok: false, error: `Pick at most ${MAX_REPORT_DAYS} days at a time.` };
  const body = { siteID, from, to };
  if (provNum !== undefined && provNum !== null && provNum !== '' && provNum !== 'all') {
    const n = positiveInt(provNum);
    if (n === null) return { ok: false, error: 'Pick a provider from the list.' };
    body.provNum = n;
  }
  return { ok: true, body, days };
}

// ── words ────────────────────────────────────────────────────────────────────────────────────────

/** "12 min", "1 hr 5 min", "2 hr"; '—' for no value. Whole minutes (the server's per-visit numbers already are). */
export function formatMinutes(min) {
  if (!isNum(min)) return '—';
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} hr ${r} min` : `${h} hr`;
}

function zonedParts(ms, tz, options) {
  if (!isNum(ms)) return null;
  const date = new Date(ms);
  if (isNaN(date.getTime())) return null;
  const run = (zone) => {
    const opts = Object.assign({}, options);
    if (zone) opts.timeZone = zone;
    return new Intl.DateTimeFormat('en-US', opts).formatToParts(date);
  };
  try {
    return run(typeof tz === 'string' && tz.trim() !== '' ? tz.trim() : null);
  } catch (e) {
    try { return run(null); } catch (err) { return null; }
  }
}

const partOf = (parts, type) => {
  const found = parts.find((p) => p.type === type);
  return found ? found.value : '';
};

/** Epoch ms of an ISO instant (with a zone), or null. */
function instantMs(iso) {
  if (typeof iso !== 'string' || iso === '') return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/** '9:15 am' — an instant at the practice (12-hour, am/pm; a report spans days, so unlike the board it says which half). '' when unusable. */
export function formatClock(iso, tz) {
  const parts = zonedParts(instantMs(iso), tz, { hour: 'numeric', minute: '2-digit', hour12: true });
  if (!parts) return '';
  const hour = Number(partOf(parts, 'hour'));
  const minute = partOf(parts, 'minute');
  const period = partOf(parts, 'dayPeriod').toLowerCase().replace(/[^a-z]/g, '');
  if (!Number.isFinite(hour) || !minute) return '';
  return `${hour === 0 ? 12 : hour}:${minute}${period ? ` ${period}` : ''}`;
}

/** '09:15' — the same instant, 24-hour, for the CSV (sorts and parses in a spreadsheet). '' when unusable. */
export function formatClock24(iso, tz) {
  const parts = zonedParts(instantMs(iso), tz, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  if (!parts) return '';
  const hour = partOf(parts, 'hour') === '24' ? '00' : partOf(parts, 'hour');
  const minute = partOf(parts, 'minute');
  return hour && minute ? `${hour}:${minute}` : '';
}

/** The practice's hour of day (0-23) for an instant, or null. */
function hourOf(iso, tz) {
  const parts = zonedParts(instantMs(iso), tz, { hour: '2-digit', hourCycle: 'h23' });
  if (!parts) return null;
  const h = Number(partOf(parts, 'hour'));
  return Number.isFinite(h) ? (h === 24 ? 0 : h) : null;
}

/** '8 am' / '12 pm' for an hour 0-23. */
export function hourLabel(h) {
  if (!isNum(h)) return '';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12} ${h < 12 ? 'am' : 'pm'}`;
}

/** True when the office set a real waiting limit (the server sends 999 for "none"). */
export function hasThreshold(thresholdMin) {
  return isNum(thresholdMin) && thresholdMin > 0 && thresholdMin < NO_THRESHOLD_MIN;
}

/** "Oct 24 – 30, 2026 · 128 visits · Dr. Shah" — the line under the title. */
export function ledeText({ from, to, visitCount, provName } = {}) {
  const parts = [rangeText(from, to)];
  if (isNum(visitCount)) parts.push(`${visitCount} ${visitCount === 1 ? 'visit' : 'visits'}`);
  if (typeof provName === 'string' && provName.trim()) parts.push(provName.trim());
  return parts.filter(Boolean).join(' · ');
}

// ── the four tiles ───────────────────────────────────────────────────────────────────────────────

/**
 * The stat tiles, with the design's copy. Numbers are the server's `tiles`; only the wording and the percent are made here.
 * "Visits over threshold" is `over · pct%`, the percent of the visits that HAD a wait (the same N the server judged).
 * @returns {Array<{key: string, label: string, value: string, sub: string, tone: string}>}
 */
export function reportTiles(report) {
  const r = obj(report);
  const t = obj(r.tiles);
  const limit = r.thresholdMin;
  const withWait = isNum(t.visitsWithWait) ? t.visitsWithWait : 0;

  let over;
  let overSub;
  if (!hasThreshold(limit)) {
    over = '—';
    overSub = 'No waiting limit is set in Settings';
  } else if (!withWait) {
    over = '0';
    overSub = `of 0 visits waited ${limit} min or more`;
  } else {
    const count = isNum(t.overThresholdCount) ? t.overThresholdCount : 0;
    over = `${count} · ${Math.round((count / withWait) * 100)}%`;
    overSub = `of ${withWait} ${withWait === 1 ? 'visit' : 'visits'} waited ${limit} min or more`;
  }

  const arrivals = isNum(t.arrivals) ? t.arrivals : 0;
  const onTime = arrivals && isNum(t.onTimeArrivals) ? `${Math.round((t.onTimeArrivals / arrivals) * 100)}%` : '—';

  return [
    { key: 'avg', label: 'Average wait', value: formatMinutes(t.avgWaitMin), sub: 'Clock starts at the later of booked time and arrival', tone: 'n' },
    { key: 'median', label: 'Median wait', value: formatMinutes(t.medianWaitMin), sub: 'Half of visits waited less', tone: 'n' },
    { key: 'over', label: 'Visits over threshold', value: over, sub: overSub, tone: hasThreshold(limit) && isNum(t.overThresholdCount) && t.overThresholdCount > 0 ? 'a' : 'n' },
    { key: 'ontime', label: 'On-time arrivals', value: onTime, sub: 'Arrived by the booked time', tone: 'n' },
  ];
}

// ── the visits table ─────────────────────────────────────────────────────────────────────────────

function patientName(v) {
  const last = typeof v.lName === 'string' ? v.lName.trim() : '';
  const first = typeof v.fName === 'string' ? v.fName.trim() : '';
  if (last && first) return `${last}, ${first}`;
  if (last || first) return last || first;
  // a PatNum missing from the roster (or from the mirror) is still a visit: never a blank cell
  if (v.patNum) return `Patient #${v.patNum}`;
  return v.aptNum ? `Visit #${v.aptNum}` : 'Unknown patient';
}

/**
 * One display row per server visit.
 * @param {Array} visits  report.visits
 * @param {{tz?: string, roomLabel?: Function, nowMs?: number, todayYmd?: string, rules?: {waitMin: Function, waitStartMs: Function, level: Function}, thresholdMin?: number}} ctx
 *   roomLabel(opNum) → the room's Practice Ops name (the report only carries the OperatoryNum); rules = waitRules.js, used ONLY for
 *   the live wait of a patient who arrived today and is not seated yet.
 */
export function visitRows(visits, ctx) {
  const c = obj(ctx);
  const tz = c.tz;
  const roomLabel = typeof c.roomLabel === 'function' ? c.roomLabel : () => '';
  const rules = obj(c.rules);

  return arr(visits).map((v) => {
    const visit = obj(v);
    const waitingToday = typeof rules.waitMin === 'function' && typeof rules.waitStartMs === 'function' && typeof rules.level === 'function'
      && isNum(c.nowMs) && c.todayYmd && visit.visitDate === c.todayYmd
      && visit.arrivedAt && !visit.seatedAt && !visit.checkoutAt && !visit.dismissedAt && !isNum(visit.waitMin);
    let waitMin = isNum(visit.waitMin) ? visit.waitMin : null;
    let level = visit.level === 'amber' || visit.level === 'red' || visit.level === 'ok' ? visit.level : null;
    let live = false;
    if (waitingToday) {
      const start = rules.waitStartMs(visit.scheduledAt, visit.arrivedAt);
      // "Early" is not a wait: the clock has not started yet (the board says "Early · appt 9:15" here)
      if (start !== null && c.nowMs >= start) {
        waitMin = rules.waitMin(visit.scheduledAt, visit.arrivedAt, null, c.nowMs);
        level = rules.level(waitMin, c.thresholdMin);
        live = waitMin !== null;
      }
    }

    const room = isNum(visit.opNum) ? (roomLabel(visit.opNum) || `Room ${visit.opNum}`) : '';
    return {
      key: `a${visit.aptNum}`,
      aptNum: visit.aptNum,
      patNum: visit.patNum || null,
      name: patientName(visit),
      date: visit.visitDate || '',
      dateText: dayText(visit.visitDate),
      provName: typeof visit.provName === 'string' ? visit.provName : '',
      room,
      bookedMin: isNum(visit.bookedMin) ? visit.bookedMin : null,
      booked: formatClock(visit.scheduledAt, tz),
      bookedMs: instantMs(visit.scheduledAt),
      arrived: formatClock(visit.arrivedAt, tz),
      arrivedMs: instantMs(visit.arrivedAt),
      late: isNum(visit.lateMin) ? visit.lateMin : null,
      lateText: isNum(visit.lateMin) && visit.lateMin > 0 ? formatMinutes(visit.lateMin) : '—',
      ready: formatClock(visit.readyAt, tz),
      readyMs: instantMs(visit.readyAt),
      seated: formatClock(visit.seatedAt, tz),
      seatedMs: instantMs(visit.seatedAt),
      waitMin,
      waitText: waitMin === null ? '—' : formatMinutes(waitMin),
      waitLevel: waitMin === null ? null : level,
      waitLive: live,
      inChairMin: isNum(visit.inChairMin) ? visit.inChairMin : null,
      inChairText: isNum(visit.inChairMin) ? formatMinutes(visit.inChairMin) : '—',
      checkout: formatClock(visit.checkoutAt, tz),
      checkoutMs: instantMs(visit.checkoutAt),
      dismissed: formatClock(visit.dismissedAt, tz),
      dismissedMs: instantMs(visit.dismissedAt),
      postOp: visit.sendEmail === true ? 'Requested' : '—',
      legacy: visit.source === 'legacy',
    };
  });
}

/** Sort keys → the row field they compare on, and whether it is text. */
export const SORT_FIELDS = Object.freeze({
  name: { field: 'name', text: true },
  provider: { field: 'provName', text: true },
  booked: { field: 'bookedMs' },
  arrived: { field: 'arrivedMs' },
  late: { field: 'late' },
  ready: { field: 'readyMs' },
  seated: { field: 'seatedMs' },
  wait: { field: 'waitMin' },
  room: { field: 'room', text: true },
  inChair: { field: 'inChairMin' },
  checkout: { field: 'checkoutMs' },
  dismissed: { field: 'dismissedMs' },
});

/** The rows ordered by a column (`dir` 1 ascending, -1 descending). A visit with no value there is ALWAYS last. No key ⇒ the server's order. */
export function sortVisitRows(rows, key, dir) {
  const list = arr(rows).slice();
  const spec = SORT_FIELDS[key];
  if (!spec) return list;
  const sign = dir === -1 ? -1 : 1;
  const empty = (v) => v === null || v === undefined || v === '';
  return list
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const x = a.row[spec.field];
      const y = b.row[spec.field];
      if (empty(x) && empty(y)) return a.index - b.index;
      if (empty(x)) return 1;
      if (empty(y)) return -1;
      const cmp = spec.text ? String(x).localeCompare(String(y), 'en', { sensitivity: 'base' }) : x - y;
      return cmp !== 0 ? cmp * sign : a.index - b.index;
    })
    .map((x) => x.row);
}

// ── charts ───────────────────────────────────────────────────────────────────────────────────────

/**
 * The "Average wait" series. A multi-day report is the server's `daily[]` (one point per day); a ONE-day report is drawn per hour,
 * as the design does for Today — the server has no hourly numbers, so those are the average `waitMin` of that day's visits grouped by
 * the practice's hour of ARRIVAL (a flagged client-side value).
 * @returns {{mode: 'day'|'hour', labels: string[], avg: Array<number|null>, over: number[], visits: number[], title: string, unit: string}}
 */
export function waitSeries(report, tz) {
  const r = obj(report);
  const daily = arr(r.daily);
  if (daily.length === 1) {
    const buckets = new Map();
    arr(r.visits).forEach((v) => {
      const visit = obj(v);
      const h = hourOf(visit.arrivedAt, tz);
      if (h === null) return;
      if (!buckets.has(h)) buckets.set(h, []);
      buckets.get(h).push(visit);
    });
    const hours = [...buckets.keys()].sort((a, b) => a - b);
    if (hours.length) {
      const fromH = hours[0];
      const toH = hours[hours.length - 1];
      const labels = [];
      const avg = [];
      const over = [];
      const counts = [];
      for (let h = fromH; h <= toH; h += 1) {
        const list = buckets.get(h) || [];
        const waits = list.map((v) => v.waitMin).filter(isNum);
        labels.push(hourLabel(h));
        avg.push(waits.length ? round1(waits.reduce((a, b) => a + b, 0) / waits.length) : null);
        over.push(list.filter((v) => v.level === 'amber' || v.level === 'red').length);
        counts.push(list.length);
      }
      return { mode: 'hour', labels, avg, over, visits: counts, title: 'Average wait', unit: 'per hour of arrival' };
    }
  }
  return {
    mode: 'day',
    labels: daily.map((d) => shortDayText(obj(d).date)),
    avg: daily.map((d) => (isNum(obj(d).avgWaitMin) ? d.avgWaitMin : null)),
    over: daily.map((d) => (isNum(obj(d).overThresholdCount) ? d.overThresholdCount : 0)),
    visits: daily.map((d) => (isNum(obj(d).visits) ? d.visits : 0)),
    title: 'Average wait',
    unit: 'per day',
  };
}

/**
 * One row per provider: the server's `providers[]` plus the average BOOKED length (flagged: derived from `visits[].bookedMin`,
 * over the visits that also have an in-chair time — the same population as the server's `avgInChairMin`).
 */
export function providerRows(report) {
  const r = obj(report);
  const visits = arr(r.visits).map(obj);
  return arr(r.providers).map((p) => {
    const prov = obj(p);
    const same = visits.filter((v) => v.provNum === prov.provNum && isNum(v.inChairMin) && isNum(v.bookedMin));
    return {
      provNum: prov.provNum,
      name: typeof prov.provName === 'string' && prov.provName.trim() ? prov.provName.trim() : `Provider #${prov.provNum}`,
      visits: isNum(prov.visits) ? prov.visits : 0,
      avgWaitMin: isNum(prov.avgWaitMin) ? prov.avgWaitMin : null,
      avgInChairMin: isNum(prov.avgInChairMin) ? prov.avgInChairMin : null,
      avgBookedMin: same.length ? round1(same.reduce((a, v) => a + v.bookedMin, 0) / same.length) : null,
    };
  });
}

/** The providers that have BOTH numbers for the "Booked vs actual chair time" bars. */
export function chairChartRows(rows) {
  return arr(rows).filter((p) => isNum(p.avgInChairMin) && isNum(p.avgBookedMin));
}

// ── CSV ──────────────────────────────────────────────────────────────────────────────────────────

/** A spreadsheet runs a cell that starts with = + - @ (or a tab / CR) as a formula; names and notes are typed by people. */
export function safeCell(value) {
  if (typeof value !== 'string') return value;
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

export const CSV_HEADERS = Object.freeze([
  'Date', 'Patient', 'Provider', 'Room', 'Booked', 'Booked length (min)', 'Arrived', 'Late (min)', 'Ready', 'Seated',
  'Waited (min)', 'In chair (min)', 'Check-out', 'Dismissed', 'Post-op email', 'Source',
]);

/**
 * The visits as a matrix for listExport.js toCsv: one row per visit, numbers as numbers (minutes), times 24-hour at the practice,
 * missing values empty. `Source` says whether the row came from the event log or from the older check-out notes (legacy).
 * @returns {{headers: string[], rows: Array<Array<string|number>>}}
 */
export function visitsCsv(visits, ctx) {
  const c = obj(ctx);
  const tz = c.tz;
  const roomLabel = typeof c.roomLabel === 'function' ? c.roomLabel : () => '';
  const rows = arr(visits).map((v) => {
    const visit = obj(v);
    const room = isNum(visit.opNum) ? (roomLabel(visit.opNum) || `Room ${visit.opNum}`) : '';
    const num = (n) => (isNum(n) ? n : '');
    return [
      visit.visitDate || '',
      safeCell(patientName(visit)),
      safeCell(typeof visit.provName === 'string' ? visit.provName : ''),
      safeCell(room),
      formatClock24(visit.scheduledAt, tz),
      num(visit.bookedMin),
      formatClock24(visit.arrivedAt, tz),
      num(visit.lateMin),
      formatClock24(visit.readyAt, tz),
      formatClock24(visit.seatedAt, tz),
      num(visit.waitMin),
      num(visit.inChairMin),
      formatClock24(visit.checkoutAt, tz),
      formatClock24(visit.dismissedAt, tz),
      visit.sendEmail === true ? 'Requested' : '',
      visit.source === 'legacy' ? 'Check-out notes (legacy)' : 'Event log',
    ];
  });
  return { headers: CSV_HEADERS.slice(), rows };
}
